import classNames from 'classnames';
import { FC, useContext, useEffect, useMemo } from 'react';
import { LocationContext } from '../../../Contexts/LocationContext';
import { Pages } from '../../../Enums';
import './DatePicker.scss';
import { useGetOrderStatus, useGetWasteStatus, useGetStoreOrderStatus, useGetProductionOrderStatus } from './Hooks/StatusHooks';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { EditStatus } from '../../../SharedTypes/Models';
import { Status, StoreOrderStatus, ProductionOrderStatus} from './utils/CalculateStatus';
import { DateAsValue } from './utils/DatePickerUtils';

type Props = {
	date: Date;
	page?: Pages;
	dateSpan?: DateAsValue[];
};

export const StatusDot: FC<Props> = ({ date, page, dateSpan}) => {
	const [selectedLocation] = useContext(LocationContext);

	const { data: wasteStatus } = useGetWasteStatus(
		format(date, 'yyyy-MM-dd'),
		selectedLocation,
		page !== Pages.WASTE_PAGE
	);
	const { data: orderStatus } = useGetOrderStatus(
		format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
		format(endOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
		selectedLocation,
		page !== Pages.ORDERS_PAGE
	);
	
	const safeStartDate = dateSpan?.[0]?.dateAsDate ?? new Date();
	const safeEndDate = dateSpan?.[4]?.dateAsDate ?? new Date();

	const { data: storeOrderStatus } = useGetStoreOrderStatus(
		format(safeStartDate, 'yyyy-MM-dd'),
		format(safeEndDate, 'yyyy-MM-dd'),
		selectedLocation,
		page !== Pages.ORDERBOOK_STORE_PAGE
	); 

	const { data: productionOrderStatus } = useGetProductionOrderStatus(
		format(safeStartDate, 'yyyy-MM-dd'),
		format(safeEndDate, 'yyyy-MM-dd'),
		selectedLocation,
		page !== Pages.ORDERBOOK_PRODUCTION_PAGE
	); 

	const getStatus = useMemo((): EditStatus => {
		switch (page) {
			case undefined:
				return 'none';

			case Pages.ORDERS_PAGE:
				return orderStatus ? Status(page, orderStatus) : 'none';

			case Pages.WASTE_PAGE:
				return wasteStatus ? Status(page, wasteStatus) : 'none';

			case Pages.PRODUCTION_PAGE:
				return 'none';

			case Pages.ORDERBOOK_STORE_PAGE:
				return storeOrderStatus ? StoreOrderStatus(page, format(date, 'yyyy-MM-dd'), storeOrderStatus) : 'none';

			case Pages.ORDERBOOK_PRODUCTION_PAGE:
				return productionOrderStatus ? ProductionOrderStatus(page, format(date, 'yyyy-MM-dd'), productionOrderStatus) : 'none';
			default:
				return 'none';
		}
	}, [orderStatus, page, wasteStatus, storeOrderStatus, productionOrderStatus]);

	return (
		<div
			className={classNames('date-picker__date-status', {
				'date-picker__date-status': getStatus === 'none',
				'date-picker__date-status--archived': getStatus === 'reported',
				'date-picker__date-status--partial': getStatus === 'partial',
			})}
		></div>
	);
};
